import { Box, Typography, keyframes } from "@mui/material";

const spinSlow = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const spinRev = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;
const pulseRing = keyframes`
  0%, 100% { opacity: 0.15; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.4; transform: translate(-50%, -50%) scale(1.06); }
`;
const scanLine = keyframes`
  0% { top: 20%; }
  100% { top: 80%; }
`;
const floatBadge = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const badges = [
  { label: "Face ID", icon: "👁", top: { xs: "4%", md: "8%" }, left: { xs: "52%", md: "60%" }, color: "#63b3ed" },
  { label: "Voice", icon: "🎙", top: { xs: "74%", md: "72%" }, left: { xs: "54%", md: "62%" }, color: "#9f7aea" },
  { label: "AI", icon: "🧠", top: { xs: "72%", md: "70%" }, left: { xs: "4%", md: "12%" }, color: "#68d391" },
];

export default function BiometricIllustration() {
  return (
    <Box sx={{ position: "relative", width: { xs: "100%", sm: 300, md: 380 }, maxWidth: 380, height: { xs: 260, sm: 300, md: 380 }, flexShrink: 0, mx: "auto", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box
          sx={{
            width: { xs: 240, sm: 280, md: 360 },
            height: { xs: 240, sm: 280, md: 360 },
            borderRadius: "50%",
            border: "1px dashed rgba(99,179,237,0.25)",
            animation: `${spinSlow} 25s linear infinite`,
            position: "relative",
          }}
        >
          {[0, 72, 144, 216, 288].map((deg, i) => (
            <Box
              key={deg}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: { xs: 8, md: 10 },
                height: { xs: 8, md: 10 },
                borderRadius: "50%",
                bgcolor: ["#63b3ed", "#9f7aea", "#68d391", "#fc8181", "#f6ad55"][i],
                transform: {
                  xs: `rotate(${deg}deg) translateX(115px) translateY(-50%)`,
                  md: `rotate(${deg}deg) translateX(180px) translateY(-50%)`,
                },
                boxShadow: "0 0 12px currentColor",
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ position: "absolute", inset: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box
          sx={{
            width: { xs: 200, sm: 240, md: 300 },
            height: { xs: 200, sm: 240, md: 300 },
            borderRadius: "50%",
            border: "1px solid rgba(159,122,234,0.2)",
            animation: `${spinRev} 15s linear infinite`,
          }}
        />
      </Box>

      {[
        { s: 200, md: 240 },
        { s: 230, md: 280 },
        { s: 260, md: 320 },
      ].map(({ s, md }, i) => (
        <Box
          key={s}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: s * 0.85, md: md * 0.85 },
            height: { xs: s * 0.85, md: md * 0.85 },
            borderRadius: "50%",
            border: "1px solid rgba(99,179,237,0.15)",
            animation: `${pulseRing} ${2 + i * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: 130, sm: 145, md: 160 },
          height: { xs: 150, sm: 165, md: 180 },
          borderRadius: 2.5,
          bgcolor: "rgba(13,17,33,0.85)",
          border: "1px solid rgba(99,179,237,0.35)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 2,
            background: "linear-gradient(90deg, transparent, #63b3ed, transparent)",
            animation: `${scanLine} 2s ease-in-out infinite alternate`,
          }}
        />
        <Box component="svg" sx={{ width: { xs: 58, md: 70 }, height: { xs: 66, md: 80 } }} viewBox="0 0 70 80">
          <ellipse cx="35" cy="38" rx="28" ry="32" fill="none" stroke="rgba(99,179,237,0.5)" strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx="24" cy="33" r="4" fill="none" stroke="#63b3ed" strokeWidth="1.5" />
          <circle cx="46" cy="33" r="4" fill="none" stroke="#63b3ed" strokeWidth="1.5" />
          <path d="M26 52 Q35 60 44 52" fill="none" stroke="#63b3ed" strokeWidth="1.5" strokeLinecap="round" />
        </Box>
        <Typography sx={{ fontSize: { xs: 9, md: 10 }, color: "#63b3ed", letterSpacing: 2, fontFamily: "monospace" }}>SCANNING…</Typography>
      </Box>

      {badges.map((b, i) => (
        <Box
          key={b.label}
          sx={{
            position: "absolute",
            top: b.top,
            left: b.left,
            bgcolor: "rgba(13,17,33,0.8)",
            border: `1px solid ${b.color}44`,
            borderRadius: 1.5,
            px: { xs: 1, md: 1.5 },
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            animation: `${floatBadge} ${2.5 + i * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.6}s`,
            maxWidth: { xs: 88, md: "none" },
          }}
        >
          <Typography component="span" sx={{ fontSize: { xs: 12, md: 14 } }}>{b.icon}</Typography>
          <Typography component="span" sx={{ fontSize: { xs: 10, md: 11 }, color: b.color, fontWeight: 600, fontFamily: "monospace" }}>
            {b.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
